import angular from "angular";

import {
  Arrays, AvatarPictureType, BootstrapStyle, calcReportType, Calculation, CalculationRowType,
  Calculations, CalculationStatus, CalculatorLogic, Dates, LegalEntityType, PaymentChannel, TaxCard2019Logic, TaxcardUiInfo, TaxDeductionWorkCategories,
  Translations, UserDefinedRow, Ajax,
  AbsenceCauseCode, InvoicesLogic
} from "@salaxy/core";

import { InvoicesService, ReportsService, SessionService, UiCrudHelpers, UiHelpers } from "../../services";
import { ApiCrudObjectControllerBindings } from "../bases";
import { CalculationCrudController } from "./CalculationCrudController";
import { CalculatorSections } from "./CalculatorSections";


/** Enumerations for calculator actions */
enum CalculatorActions {
  AllowForcePay = "allowForcePay",
  Approve = "approve",
  Cancel = "cancel",
  CopyAsNew = "copyAsNew",
  Delete = "delete",
  Pay = "pay",
  Recalculate = "recalculate",
  Reject = "reject",
  Save = "save",
  SendToPro = "sendToPro",
  ShowPaymentDialog = "showPaymentDialog",
  SetSalaryDateKind = "setSalaryDateKind",
  ContinuePsdPayment = "continuePsdPayment",
}

class Calculator2019ControllerCrudBindings extends ApiCrudObjectControllerBindings {
  /** Options for the behavior of the calculator when in anonymous (demo) mode. */
  public anonOptions = "<";
}


/**
 * Year 2019 Calculator controller that is not dependent on sigleton Calculations service.
 * This controller extends ApiCrudObjectController (via CalculationCrudController).
 */
export class Calculator2019Controller extends CalculationCrudController {

  /** Component bindings */
  public static crudBindings = new Calculator2019ControllerCrudBindings();

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["Calculations", "UiHelpers", "UiCrudHelpers", "$location", "$routeParams", "SessionService", "ReportsService", "InvoicesService", "AjaxNg1"];

  /** Type of the chart to show by default. */
  public chartType = "bar";

  /** Options for the behavior of the calculator when in anonymous (demo) mode. */
  public anonOptions = {
    role: "company" as "company" | "household",
  };


  /** Flags to indicate if the request is in progress */
  public loaders: any = {};


  constructor(
    private calcApi: Calculations,
    uiHelpers: UiHelpers,
    protected uiCrudHelpers: UiCrudHelpers,
    $location: angular.ILocationService,
    $routeParams: any,
    sessionService: SessionService,
    reportsService: ReportsService,
    protected invoicesService: InvoicesService,
    private ajax: Ajax
  ) {
    // Dependency injection
    super(calcApi, reportsService, uiHelpers, $location, $routeParams, sessionService, invoicesService);
  }
  /**
   * Initialization code.
   */
  public $onInit() {
    super.$onInit();
  }

  /** Returns the current. Called currentCalc for backward compatibility with the old views. */
  public get currentCalc(): Calculation {
    return this.current;
  }

  /**
   * Gets the Sections object for the current calculation.
   * It keeps track of which section is currently shown and what text etc. is shown in that section.
   */
  public get calcSections(): CalculatorSections {
    return new CalculatorSections(this.current);
  }

  /**
   * Gets or sets the isHouseholdDeductible.
   * This will be moved to a different location in V03, hence the abstraction.
   */
  public get isHouseholdDeductible(): boolean {
    if (!this.current) {
      return false;
    }
    return this.current.salary.isHouseholdDeductible;
  }
  public set isHouseholdDeductible(value: boolean) {
    this.current.salary.isHouseholdDeductible = value;
  }

  /**
   * Gets or sets the taxDeductionCategory (used to be salary.taxDeductionCategories).
   * This will be moved to a different location in V03, hence the abstraction.
   */
  public get taxDeductionCategory(): TaxDeductionWorkCategories {
    return this.current.salary.taxDeductionCategories;
  }
  public set taxDeductionCategory(value: TaxDeductionWorkCategories) {
    this.current.salary.taxDeductionCategories = value;
  }

  /**
   * Returns true if the calculator is in "household" mode (as opposed to default "company" mode).
   */
  public get isHousehold() {
    if (this.sessionService.isInRole("pro") && this.sessionService.isInRole("person")) {
      return true; // All person created calaulations are considered pro for now.
    }
    if (this.sessionService.isInRole("worker")) {
      return false; // Uses calculator for companies in old Worker site (to be replaced by Omapalkka)
    }
    if (this.sessionService.isInRole("anon")) {
      if (this.anonOptions && this.anonOptions.role === "household") {
        return true;
      }
      return false;
    }
    return this.sessionService.isInRole("household");
  }


  /**
   * If true, the given action (button) is displayed.
   * @param action One of the action values.
   * For now, validation and role based action logic is in UI.
   * Todo: some edge cases, i.e. isForcePayAllAllowed (for pay-button) are still in UI.
   */
  public showAction(action: CalculatorActions): boolean {
    if (!this.current) {
      return false;
    }
    // In read only mode
    if (this.isReadOnlyForced && action != CalculatorActions.ShowPaymentDialog) {
      return false;
    }
    if (this.isReadOnlyForced && action == CalculatorActions.ShowPaymentDialog) {
      return true;
    }
    if (this.current.workflow.status == CalculationStatus.PayrollDraft) {
      // Not possible to make changes to editable payroll calculation
      return false;
    }
    if (this.current.isReadOnly) {
      switch (action) {
        case CalculatorActions.Cancel:
          return this.current.workflow.status == CalculationStatus.PaymentStarted &&
            (
              [
                PaymentChannel.PalkkausManual,
              PaymentChannel.TalenomManual,
                PaymentChannel.PalkkausPersonal,
                PaymentChannel.PalkkausInvoices
              ].indexOf(this.current.info.paymentChannel) > -1 ||
              (
                InvoicesLogic.isCfa(this.current.info.paymentChannel) &&
                !this.current.info.payrollId
              )
            );
        case CalculatorActions.CopyAsNew:
          return !this.isReadOnlyForced; // Not needed?
        case CalculatorActions.ShowPaymentDialog:
          return true;
        case CalculatorActions.ContinuePsdPayment:
          return this.current.info.paymentChannel == PaymentChannel.PalkkausPSD && this.current.workflow.status == CalculationStatus.PaymentStarted;
        case CalculatorActions.AllowForcePay: // "Allow forcepay" action is payment action when calculation is not valid. Logic handled in UI for now.
        case CalculatorActions.Pay:
        case CalculatorActions.Recalculate:
        default:
          return false;
      }
    } else {
      switch (action) {
        case CalculatorActions.AllowForcePay:
          //TODO: refactor this to contain only allowed status, not disallowed
          return this.isForcePayAllAllowed &&
            (this.current.workflow.status != CalculationStatus.WaitingApproval &&
              this.current.workflow.status != CalculationStatus.SharedRejected &&
              this.current.workflow.status != CalculationStatus.SharedWaiting &&
              this.current.workflow.status != CalculationStatus.Repair
            );
        case CalculatorActions.Pay:
          // add here isValid?
          return this.current.workflow.status == CalculationStatus.Draft ||
            this.current.workflow.status == CalculationStatus.ProDraft ||
            this.current.workflow.status == CalculationStatus.SharedApproved;
        case CalculatorActions.Save:
          return this.current.workflow.status == CalculationStatus.Draft ||
            this.current.workflow.status == CalculationStatus.ProDraft ||
            this.current.workflow.status == CalculationStatus.SharedApproved ||
            this.current.workflow.status == CalculationStatus.History ||
            this.current.workflow.status == CalculationStatus.Repair;
        case CalculatorActions.Delete:
          return this.current.workflow.status == CalculationStatus.Draft ||
            this.current.workflow.status == CalculationStatus.ProDraft ||
            this.current.workflow.status == CalculationStatus.SharedRejected ||
            this.current.workflow.status == CalculationStatus.History ||
            this.current.workflow.status == CalculationStatus.Repair;
        case CalculatorActions.SendToPro:
          return this.current.workflow.status == CalculationStatus.WaitingApproval;
        case CalculatorActions.Recalculate:
          return true;
        case CalculatorActions.Reject:
          return this.current.workflow.status == CalculationStatus.SharedApproved ||
            this.current.workflow.status == CalculationStatus.SharedWaiting;
        case CalculatorActions.Approve:
          return this.current.workflow.status == CalculationStatus.SharedRejected ||
            this.current.workflow.status == CalculationStatus.SharedWaiting;
        case CalculatorActions.SetSalaryDateKind:
          return (this.current.workflow.status == CalculationStatus.Draft ||
            this.current.workflow.status == CalculationStatus.ProDraft ||
            this.current.workflow.status == CalculationStatus.SharedApproved) &&
            (
              InvoicesLogic.isPalkkausManualLike(this.current.info.paymentChannel) ||
              InvoicesLogic.isPalkkausInvoicesLike(this.current.info.paymentChannel) ||
              this.current.info.paymentChannel == PaymentChannel.PalkkausCfaTest ||
              this.current.info.paymentChannel == PaymentChannel.Briox ||
              this.current.info.paymentChannel == PaymentChannel.Askelma ||
              this.current.info.paymentChannel == PaymentChannel.PasseliMerit ||
              this.current.info.paymentChannel == PaymentChannel.Ecom ||
              this.current.info.paymentChannel == PaymentChannel.Hausvise
            );
        default:
          return false;
      }
    }

  }

  /**
   * Returns true, if action (button) is disabled
   * TODO: Add rules based on roles and validation? Pay button logic is in UI.
   */
  public isActionDisabled(action: CalculatorActions): boolean {
    if (!this.current) {
      return true;
    }
    // In read only mode
    if (this.isReadOnlyForced) {
      return true;
    }

    if (this.current.isReadOnly) {
      switch (action) {
        case CalculatorActions.Delete:
          return this.current.workflow.status == CalculationStatus.PaymentStarted;
        case CalculatorActions.Save:
          // add here isValid?
          return this.current.workflow.status == CalculationStatus.PaymentStarted;
        default:
          return true;
      }
    }
  }
  /**
   * Gets the first row of the calculation assuring that it exists
   * If there is no current calculation, the calculation is not added.
   */
  public getFirstRow(): UserDefinedRow {
    return CalculatorLogic.getFirstRow(this.current);
  }

  /**
   * Takes the current calculation, creates a copy of it in draft state
   * and sets that calculation as current.
   * This is done typically, when a calculation was in Paid state.
   */
  public copyAsNew() {
    const copy = this.copyItem(this.current);
    this.calcApi.recalculate(copy).then((calc) => {
      this.setCurrentRef(calc);
      if ((this as any)?.worktimeCtrl) {
        (this as any).worktimeCtrl.reset();
      }
      else if ((this.parentController as any)?.worktimeCtrl) {
        (this.parentController as any).worktimeCtrl.reset();
      }
      this.calcSections.setActive("worktime");
    });
  }

  /**
   * Reset worker in the calculations.
   */
  public resetWorker() {
    this.currentCalc.worker = CalculatorLogic.getBlank().worker;
  }

  /**
   * Gets misc. logic calculations and operations (client side, based on rows etc.)
   * that affect how the user interface is rendered.
   */
  public get calcLogic() {
    if (!this.current) {
      return {};
    }
    const workerCalc = ((this.current.result || {}).workerCalc || {});
    return {
      /** If true, calcualtion contains compensation. This is useful for customizing texts and other UI elements */
      hasCompensation: !!this.current.rows.find((x) => x.rowType === CalculationRowType.Compensation),
      /** Expenses box total. TODO: We should see can we move this to result / server side! */
      expensesTotal: workerCalc.prepaidExpenses + workerCalc.deductions - workerCalc.unemploymentInsurance - workerCalc.pension - workerCalc.tax,

      /** Sum for child care subsidy. This is shown separately in the UI. */
      childCareSubsidy: (this.current.result && this.current.result.rows) ? Arrays.sum(this.current.result.rows.filter((x) => x.rowType === CalculationRowType.ChildCareSubsidy), (x) => x.total) : null,
    };
  }

  /**
   * Opens the login form for the current user
   * @param redirectUrl - The URL where the user is taken after a successfull login.
   * @param role - Optional role (household, worker or company) for the situations where it is known - mainly for new users
   */
  public signIn(redirectUrl: string = null, role: string = null) {
    this.sessionService.signIn(redirectUrl, role);
  }

  /**
   * Opens the login dialog with signUp / register functionality
   * @param redirectUrl - The URL where the user is taken after a successfull login
   * @param role - Optional role (household, worker or company) for the situations where it is known - mainly for new users
   */
  public register(redirectUrl: string = null, role: string = null) {
    this.sessionService.register(redirectUrl, role);
  }

  /**
   * Shows a report for the current calculation using a modal dialog.
   * @param reportType - Type of report to show
   */
  public showReportDialog(reportType: calcReportType) {
    this.reportsService.showReportModalForCalc(reportType, this.current);
  }

  /**
   * Deletes the currently selected calculation
   */
  public deleteCalc(path: string) {
    this.uiHelpers.showConfirm("SALAXY.NG1.CalcComponent.dialog.deleteCalc")
      .then((result: boolean) => {
        if (result) {
          if(this.isNew()){
            this.$location.path(path || "/");
          } else {
            const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
            this.deleteNoConfirm(this.current).then(() => {
            this.setCurrentRef(this.api.getBlank());
            loading.dismiss();
            this.$location.path(path || "/");
          });
          }
        }
      });
  }

  /**
   * Recalculates the current calculation using the API.
   * @returns A Promise with result data (Calculation)
   */
  public recalculate(): Promise<Calculation> {
    // Sets the accounts before recalculate and not after in setCurrent.
    if (this.sessionService.isInRole("worker")) {
      this.current.worker.isSelf = true;
    } else {
      this.current.employer.isSelf = true;
    }
    return this.calcApi.recalculate(this.current).then((calc) => {
      // TODO: Check that the spinner works here. Also remove unnecessary spinners in other places.
      super.setCurrentValue(calc);
      return calc;
    });
  }

  /**
   * Sets the current item as the given object (reference to a new object).
   * This also resets the original item and sets the state as loaded.
   * Override also sets either employer or worker based on the current role.
   * @param item The new current item.
   */
  public setCurrentRef(item: Calculation) {
    if (item) {
      if (this.sessionService.isInRole("worker")) {
        item.worker.isSelf = true;
      } else {
        item.employer.isSelf = true;
      }
    }
    super.setCurrentRef(item);
  }

  /** Overrides the save method to allow anonymous saving to local storage */
  public save() {
    /** Not sure whether the local storage saving is enabled anywhere? */
    if (!this.sessionService.isAuthenticated) {
      this.current.id = "anon";
      return this.recalculate().then(() => {
        this.current.worker.accountId = "anon";
        this.current.worker.avatar.id = this.current.worker.accountId;
        this.current.worker.avatar.color = "lime";
        this.current.worker.avatar.displayName = "SALAXY.NG1.CalcComponent.saveNotAuthenticatedDisplayName";
        this.current.worker.avatar.entityType = LegalEntityType.Person;
        this.current.worker.avatar.pictureType = AvatarPictureType.Icon;
        this.setToLocalStorage(this.current);
        return this.current;
      });
    } else {
      if (this.current.worker.accountId === "anon") {
        this.current.worker.accountId = null;
      }
      if (!this.current.worker.accountId) {
        this.deleteFromLocalStorage();
      }
      if (this.current.id === "anon") {
        this.current.id = null;
      }
      return super.save();
    }
  }

  /**
   * Returns validation for current calculation or empty validation object if the calculation has not been recalculated (validated).
   */
  public get validation() {
    return this.currentCalc?.result?.validation || { errors: [], hasAllRequiredFields: true, isValid: true };
  }

  /** Returns date range text. */
  public getDatesRangeText() {
    if (!this.currentCalc.info.workStartDate || !this.currentCalc.info.workEndDate) {
      return "SALAXY.NG1.CalcComponent.getDatesRangeText";
    }
    return Dates.getFormattedRange(this.currentCalc.info.workStartDate, this.currentCalc.info.workEndDate)
      + ` (${this.currentCalc.framework.numberOfDays}\xa0${Translations.get("SALAXY.NG1.CalcComponent.getUsecaseTextNumberOfDays")})`; // "\xa0" is non-breaking space.
  }

  /** Returns household usecase text */
  public getUsecaseText() {
    if (!this.currentCalc.usecase && !this.currentCalc.framework.numberOfDays) {
      return "SALAXY.NG1.CalcComponent.getUsecaseText";
    }
    if (this.currentCalc.usecase && !this.currentCalc.framework.numberOfDays) {
      return ` ${this.currentCalc.usecase.label}.` + Translations.get("SALAXY.NG1.CalcComponent.getUsecaseText2");
    }
    return Dates.getFormattedRange(this.currentCalc.info.workStartDate, this.currentCalc.info.workEndDate)
      + ` (${this.currentCalc.framework.numberOfDays} ${Translations.get("SALAXY.NG1.CalcComponent.getUsecaseTextNumberOfDays")}, ${this.currentCalc.usecase.label})`;
  }

  /** Returns true if the payment is possible when the employer pays all.  */
  public get isForcePayAllAllowed(): boolean {
    const obj = this.validation.errors.find((x) =>
      (x.type !== "warning"));
    return !obj;
  }

  /**
   * Shows a dialog for adding a new Employment relation.
   * If user goes through the wizard the employment relation is saved and set to the calculation.
   */
  public showNewWorkerDialog() {
    this.uiCrudHelpers.createNewEmployment().then((result) => {
      if (result.action === "ok") {
        this.updateFromEmployment(result.item.id, true);
      }
    });
  }

  /**
   * Shows a dialog during the check of auto taxcard.
   */
  public showRefreshAutoTaxcardDialog() {
    this.loaders["showRefreshAutoTaxcardDialog"] = true;
    this.uiCrudHelpers.refreshAutoTaxcard(this.current.worker.tax?.taxcardId, this.current.workflow?.salaryDate).then(() => {
      this.recalculate();
      this.loaders["showRefreshAutoTaxcardDialog"] = false;
    });
  }

  /**
   * Shows a dialog for modifying an existing Worker:
   * Saves changes and updates the calculation.
   */
  public showWorkerEditDialog() {
    this.uiCrudHelpers.openEditEmploymentDialog(this.current.worker.employmentId, "updateCalc")
      .then((result) => {
        if (result.action === "ok" || result.action === "ok-no-rows") {
          const loader = this.uiHelpers.showLoading("SALAXY.NG1.CalcComponent.dialog.showWorkerEditDialogLoading");
          this.updateFromEmployment(result.item.id, result.action === "ok").then(() => {
            loader.dismiss();
          });
        }
      });
  }

  /** UI related data and texts about the taxcard. */
  public get taxUi(): TaxcardUiInfo {
    return TaxCard2019Logic.getCalcTaxcardInfo(this.current);
  }

  /**
   * Gets the text for pension.
   * @param isOverview If true, returns the shorter text for overview.
   */
  public getPensionText(isOverview = false) {
    if (this.current?.result?.responsibilities?.pensionCalculation) {
      return Translations.get(`SALAXY.ENUM.PensionCalculation.${this.current.result.responsibilities.pensionCalculation}.${isOverview ? "labelForIncome" : "labelWithEnumLabel"}`);
    }
  }

  /**
   * Sends the payroll from company to PRO.
   * @param message Message to the accountant
   */
  public sendToPro(message: string) {
    const loader = this.uiHelpers.showLoading("SALAXY.NG1.CalcComponent.dialog.sendToProSending", "SALAXY.NG1.CalcComponent.dialog.sendToProSendingDescr");
    this.current.workflow.status = CalculationStatus.ProDraft;
    this.save().then(() => {

      this.api.saveWorkflowEvent(this.current, {
        type: "PartnerMessageClosed",
        ui: BootstrapStyle.Success,
        message: message ? Translations.get("SALAXY.NG1.CalcComponent.dialog.sendToProMessage") + message : Translations.get("SALAXY.NG1.CalcComponent.dialog.sendToProMessageChecked"),
      }).then(() => {
        loader.dismiss();
        this.$location.path("/service");
      });
    });
    return;
  }

  /**
   * Calculates the total amount of days for the given period type.
   * @param periodType Period type to calculate days for.
   * @returns Sum of days for the given period type.
   */
  public calculatePeriodDays(periodType: "absencesDays"  | "leavesDays" | "paidAbsencesDays") {
    switch (periodType) {
      case "leavesDays":
        return Arrays.sum(this.currentCalc.worktime?.absencesPeriods?.filter(x => x.causeCode == AbsenceCauseCode.AnnualLeave) ?? [], (x) => x.period.daysCount);
      case "absencesDays":
        return Arrays.sum(this.currentCalc.worktime?.absencesPeriods?.filter(x => x.causeCode != AbsenceCauseCode.AnnualLeave && !x.isPaid) ?? [], (x) => x.period.daysCount);
      case "paidAbsencesDays":
        return Arrays.sum(this.currentCalc.worktime?.absencesPeriods?.filter(x => x.causeCode != AbsenceCauseCode.AnnualLeave && x.isPaid) ?? [], (x) => x.period.daysCount);
    }
  }

  /**
   * Saves a calculation to local storage
   * @param calc - Calculation to store
   */
  private setToLocalStorage(calc: any): void {
    if (window.localStorage) {
      window.localStorage.setItem("salaxy-calculation", JSON.stringify(calc));
    }
  }

  /**
   * Deletes a calculation in local storage
   */
  private deleteFromLocalStorage(): void {
    if (window.localStorage) {
      window.localStorage.removeItem("salaxy-calculation");
    }
  }
}
